import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { enqueueSnackbar } from 'notistack';
import { Dialog, DialogTitle, DialogContent, Typography, Grid, TextField, Alert, Tooltip } from '@mui/material';
import LoadingAnimation from '../../../reusable-components/loader/LoadingAnimation';
import axiosInstance from '../../../utils/axios';
import CloseIconButton from '../../../reusable-components/basic-buttons/CloseIconButton';
import { fDate } from '../../../utils/formatTime';
import { fCurrency } from '../../../utils/formatNumber';
import { updatePreviousInvoicingWithNewBookingVendorStatus } from '../../../redux/slices/invoicing';
import { useDispatch } from '../../../redux/store';

InvoiceStatusModalDialog.propTypes = {
  id: PropTypes.number,
  total: PropTypes.number,
  vendor: PropTypes.string,
  bookingVendorStatus: PropTypes.string,
  close: PropTypes.func,
};

const RowItem = ({ value, title, md = 4, tooltip }) => <Grid item xs={12} md={md}>
  <Typography variant="subtitle1">{title}</Typography>
  <Tooltip title={tooltip ?? ""} disableHoverListener={!tooltip}>
    <TextField size="small" fullWidth inputProps={{ input: { readOnly: true } }} value={value ?? ""} sx={{ "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { border: "black 1px solid" } } }} />
  </Tooltip>
</Grid>;

export default function InvoiceStatusModalDialog({ id, total, vendor, bookingVendorStatus, close }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const paymentFieldsUnavailable = data?.hasPaymentApiPermission === false;
  const paymentPermissionTooltip = "Payment details cannot be displayed because your Intacct API permissions do not allow access to this information.";

  useEffect(() => {
    const abortController = new AbortController();
    axiosInstance.post(`/Invoices/InvoiceStatus?id=${id}`, null, { signal: abortController.signal })
      .then(res => {
        if (res.data) {
          setData(res.data);
          if (bookingVendorStatus !== res.data.state) dispatch(updatePreviousInvoicingWithNewBookingVendorStatus({ invoiceId: id, status: res.data.state }));
        }
        else enqueueSnackbar("Failed to load data", { variant: "error" });
      })
      .catch((ress) => {
        if (!ress?.startsWith("Request canceled")) enqueueSnackbar("Failed to load data", { variant: "error" })
      })
      .finally(() => setLoading(false));
    return () => abortController.abort();
  }, []);

  return <Dialog open onClose={(event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    close();
  }} fullWidth maxWidth="md">
    <DialogTitle sx={{ textAlign: 'center' }}>
      Invoice Status
      <CloseIconButton onClick={close} />
    </DialogTitle>
    <DialogContent>
      {loading ? <LoadingAnimation /> :
        <Grid container spacing={1.5}>
          <RowItem value={fCurrency(total)} title="Invoice Total" />
          <RowItem value={fCurrency(data.totalPaid)} title="Total Paid" />
          <RowItem value={fCurrency(data.totalDue)} title="Total Due" />
          <RowItem value={data.state} title="Status" md={6} />
          <RowItem
            value={paymentFieldsUnavailable ? "Unavailable" : data.docOrCheckingNum}
            title="Document/Check no"
            md={6}
            tooltip={paymentFieldsUnavailable ? paymentPermissionTooltip : null}
          />
          <RowItem value={vendor} title="Invoice Vendor" md={6} />
          <RowItem value={data.vendor} title="Booked To Vendor" md={6} />
          <RowItem value={fDate(data.glDate, "MMMM, dd, yyyy")} title="GL Date" md={3} />
          <RowItem value={fDate(data.dueDate, "MMMM, dd, yyyy")} title="Due Date" md={3} />
          <RowItem value={fDate(data.paidDate, "MMMM, dd, yyyy")} title="Paid Date" md={3} />
          <RowItem value={fDate(data.dateCreatedByBookingVendor, "MMMM, dd, yyyy")} title="Date Submitted" md={3} />
          <RowItem
            value={paymentFieldsUnavailable ? "Unavailable" : data.paymentType}
            title="Payment Type"
            md={6}
            tooltip={paymentFieldsUnavailable ? paymentPermissionTooltip : null}
          />
          <RowItem
            value={paymentFieldsUnavailable ? "Unavailable" : data.paymentUrl}
            title="Payment URL"
            md={6}
            tooltip={paymentFieldsUnavailable ? paymentPermissionTooltip : null}
          />
          <RowItem value={data.description} title="Description" md={12} />
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              Link
            </Typography>
            <Typography variant="body2" sx={{ color: 'blue', cursor: 'pointer' }} onClick={() => window.open(data.link, '_blank')}>
              {data.link ? data.link : ''}
            </Typography>
          </Grid>
          {data.onHold && <Grid item xs={12}><Alert sx={{ height: '35px', fontSize: 12, py: 0, px: 1, m: 0 }} severity="error">Invoice on hold</Alert></Grid>}
          {data.glItems && <>
            <Typography variant="subtitle1" sx={{ width: "100%", mt: 1, textAlign: "center" }}>
              GL Items
            </Typography>
            {data.glItems.map((item, index) => <Grid key={index} item container spacing={1.5}>
              <RowItem value={item.facility} title="Facility" md={3} />
              <RowItem value={item.glAccount} title="Account" md={3} />
              <RowItem value={item.department} title="Department" md={3} />
              <RowItem value={fCurrency(item.amount)} title="Amount" md={3} />
              {item.memo && <RowItem value={item.memo} title="Memo" md={12} />}
            </Grid>)}
          </>}
        </Grid>}
    </DialogContent>
  </Dialog>;
}